import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import DeviceCasePreselectionDiagnoseList from './DeviceCasePreselectionDiagnoseList';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Tooltip, IconButton } from '@material-ui/core';
import {
    diagnoseCaseExecutionTypesLookup,
    diagnoseCaseGroupsLookup,
    allowedDiagnoseCaseParamsPerClientType,
} from '../../AdminDiagnoseCase';
import { AssessmentOutlined, Autorenew } from '@material-ui/icons';
import { createHistory, hasPermission } from '../../../sc-utilities';
import { Redirect } from 'react-router-dom';
import * as actions from '../store/deviceCasePreselectionActions';

const styles = (theme) => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
    },
    label: {
        flexDirection: 'column',
    },
    iconText: {
        fontSize: '10px',
    },
});

allowedDiagnoseCaseParamsPerClientType['work'].push('keyboard', 'motherboard');

const DeviceCasePreselectionDiagnoseCases = (props) => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, activeCustomerID, onResetDiagnoseCaseParams } = props;
    const [redirectOptions, setRedirectOptions] = useState({
        redirect: false,
        rowData: null,
        redirectPath: '',
    });

    const visible = columns && columns['DeviceCasePreselectionDiagnoseCases'];

    const groupLookups = diagnoseCaseGroupsLookup(translations);
    const executionTypesLookups = diagnoseCaseExecutionTypesLookup(translations);

    const resetDiagnoseCaseParams = async (rowData) => {
        await onResetDiagnoseCaseParams({ DeviceCasePreselectionID: rowData.DeviceCasePreselectionDiagnoseCase.DeviceCasePreselectionID, DiagnoseCaseID: rowData.DiagnoseCaseID, ActiveCustomerID: activeCustomerID });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations['action'] || 'Action',
                field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: (rowData) => (
                    <>
                        {hasPermission(permissions, 'device_case_preselection_update') ? (
                            <Tooltip title={translations['update_params'] || 'Update params'}>
                                <IconButton
                                    value={rowData.Key}
                                    disabled={
                                        !allowedDiagnoseCaseParamsPerClientType[rowData.ClientType].includes(
                                            rowData.Key
                                        )
                                    }
                                    aria-label={translations['update_params'] || 'Update params'}
                                    onClick={() =>
                                        redirectHandler(`/settings/device-case-preselection/update-case-params`, rowData)
                                    }
                                >
                                    <AssessmentOutlined />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            ''
                        )}
                        {hasPermission(permissions, 'device_case_preselection_update') ? (
                            <Tooltip title={translations['reset_params'] || 'Reset params'}>
                                <IconButton
                                    value={rowData.Key}
                                    disabled={
                                        !allowedDiagnoseCaseParamsPerClientType[rowData.ClientType].includes(
                                            rowData.Key
                                        )
                                    }
                                    aria-label={translations['reset_params'] || 'Reset params'}
                                    onClick={() => resetDiagnoseCaseParams(rowData)}
                                >
                                    <Autorenew />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            ''
                        )}
                    </>
                ),
            },
            {
                title: translations['long_name'] || 'Long name',
                field: 'LongName',
                hidden: visible ? visible['LongName'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.LongName ? rowData.LongName : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['short_name'] || 'Short name',
                field: 'ShortName',
                hidden: visible ? visible['ShortName'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.ShortName ? rowData.ShortName : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['execution_type'] || 'Execution type',
                field: 'ExecutionType',
                hidden: visible ? visible['ExecutionType'] : false,
                filtering: true,
                filterCellStyle: { maxWidth: '150px' },
                lookup: executionTypesLookups,
                defaultFilter: Object.keys(executionTypesLookups),
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.ExecutionType
                                ? translations[rowData.ExecutionType] || rowData.ExecutionType
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['client_type'] || 'Client type',
                field: 'ClientType',
                hidden: visible ? visible['ClientType'] : false,
                filtering: true,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.ClientType
                                ? translations[rowData.ClientType] || rowData.ClientType
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['group'] || 'Group',
                field: 'Group',
                hidden: visible ? visible['Group'] : false,
                filtering: true,
                filterCellStyle: { maxWidth: '150px' },
                lookup: groupLookups,
                defaultFilter: Object.keys(groupLookups),
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Group
                                ? translations[rowData.Group] || rowData.Group
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['visible'] || 'Visible',
                field: 'Visible',
                hidden: visible ? visible['Visible'] : false,
                filtering: true,
                lookup: { 1: translations['yes'] || 'Yes', 0: translations['no'] || 'No' },
                defaultFilter: ['0', '1'],
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Visible ? translations['yes'] || 'Yes' : translations['no'] || 'No'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['key'] || 'Key',
                field: 'Key',
                hidden: visible ? visible['Key'] : true,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Key ? rowData.Key : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible]);

    const redirectHandler = (path, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, rowData: rowData, redirectPath: path });
    };
    if (redirectOptions.redirect) {
        return (
            <Redirect
                to={{
                    pathname: redirectOptions.redirectPath.split('?')[0],
                    search: redirectOptions.redirectPath.split('?')[1],
                    state: { redirectOptions },
                }}
            />
        );
    }
    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'device_case_preselection_list') && (
                <DeviceCasePreselectionDiagnoseList tableColumns={tableColumns} tableRef={tableRef} />
            )}
        </div>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        onResetDiagnoseCaseParams: (params) => dispatch(actions.resetDiagnoseCaseParams(params)),
    };
};

const mapStateToProps = (state) => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(DeviceCasePreselectionDiagnoseCases));
